'use client'

import { useForm } from 'react-hook-form'

import {
  buildEditAssertionWrite,
  toPlainAmount,
} from '~/app/accounting/lot-assertion-writes'
import { LotRow } from '~/app/accounting/lot-rows'
import { useLotAssertionWrite } from '~/app/accounting/useLotAssertionWrite'
import { DateInput, NumberInput } from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { LabelValue } from '~/components/component-library/LabelValue'

type EditLotFormValues = {
  unitPrice: string
  acquisitionTime: Date | null
  quantity: string
}

/**
 * A sparse patch over the lot's current values with quantity locked (the
 * account's balance is ground truth): only fields that differ are asserted,
 * so an untouched field stays derived.
 */
export const EditLotDialog = ({ lot }: { lot: LotRow }) => {
  const { writeAssertion, isProcessing } = useLotAssertionWrite()

  const { control, watch } = useForm<EditLotFormValues>({
    mode: 'onChange',
    defaultValues: {
      unitPrice: toPlainAmount(lot.unitPrice),
      acquisitionTime: new Date(lot.acquisitionTime),
      quantity: toPlainAmount(lot.quantity),
    },
  })

  const values = watch()
  // Undefined until submittable, driving both the disabled state and the body.
  const write = buildEditAssertionWrite({
    lot,
    unitPrice: values.unitPrice ?? '',
    acquisitionTime: values.acquisitionTime,
  })

  const submit = async () => {
    if (!write) return
    await writeAssertion({
      assertion: write,
      successMessage: 'Lot edit saved',
      successDescription: 'Your changes will be applied to your totals soon',
      errorMessage: 'Failed to save lot edit',
    })
  }

  return (
    <SplitsDialogContent
      id="editLot"
      variant="cta"
      title={`Edit lot ${lot.lotNumber}`}
      isLoading={isProcessing}
      actionButton={{
        text: 'Save edit',
        onClick: submit,
        isDisabled: write === undefined,
      }}
      cancelButton={{ text: 'Cancel' }}
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-secondary">
          Your corrections are saved and kept even as your books refresh. Fields
          you leave unchanged keep their original values.
        </p>

        <div className="flex flex-col gap-2">
          <LabelValue
            size="small"
            label="Wallet"
            value={lot.smartAccountName}
          />
          <LabelValue
            size="small"
            label="Token"
            value={lot.symbol || lot.tokenAddress}
          />
        </div>

        <NumberInput
          control={control}
          inputName="unitPrice"
          label="Lot price"
          description="USD per token at acquisition"
          prefix="$"
          decimalScale={18}
          placeholder="0.00"
          autoFocus
        />
        <DateInput
          control={control}
          inputName="acquisitionTime"
          label="Date received"
          maxDate={new Date()}
          showTimeSelect
        />
        <NumberInput
          control={control}
          inputName="quantity"
          label="Amount"
          unit={lot.symbol || undefined}
          decimalScale={lot.decimals}
          placeholder="0"
          disabled
          disabledReason="The amount comes from the account's actual balance and can't be asserted"
        />
      </div>
    </SplitsDialogContent>
  )
}
